<template>
  <nav
    ref="navRef"
    class="top-nav"
    :style="navStyle"
    @mouseenter="isHovering = true"
    @mouseleave="isHovering = false"
  >
    <div class="nav-inner">
      <button
        class="menu-toggle"
        :class="{ open: isMobileMenuOpen }"
        type="button"
        aria-label="Toggle navigation menu"
        @click.stop="toggleMobileMenu"
      >
        <span></span>
        <span></span>
        <span></span>
      </button>

      <div class="nav-scroll" :class="{ 'is-open': isMobileMenuOpen }">
        <transition name="mobile-menu">
          <ul v-if="!isCompact || isMobileMenuOpen" class="nav-links">
            <li :class="{ active: currentPage === 'home' }" @click="handleNavClick('home')">{{ $t('nav.home') }}</li>
            <li :class="{ active: currentPage === 'projects' }" @click="handleNavClick('projects')">{{ $t('nav.projects') }}</li>
            <li :class="{ active: currentPage === 'games' }" @click="handleNavClick('games')">{{ $t('nav.games') }}</li>
            <li :class="{ active: currentPage === 'contact' }" @click="handleNavClick('contact')">{{ $t('nav.contact') }}</li>
            <li class="nav-external">
              <a href="https://obratycoon.com.br/" target="_blank" rel="noopener noreferrer" @click="closeMobileMenu">ObraTycoon</a>
            </li>
          </ul>
        </transition>
      </div>

      <div class="lang-toggle">
        <span :class="{ active: locale === 'pt' }" @click="setLang('pt')">
          PT
        </span>
        <span :class="{ active: locale === 'en' }" @click="setLang('en')">
          EN
        </span>
      </div>
    </div>
  </nav>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import { useI18n } from 'vue-i18n'

const { locale } = useI18n()
const route = useRoute()
const router = useRouter()
const emit = defineEmits(['request-reset'])

const setLang = (lang) => {
  locale.value = lang
  localStorage.setItem('lang', lang)
  closeMobileMenu()
}

const navTargets = {
  home: '/',
  games: '/games',
  projects: '/projects',
  contact: '/contact'
}

const props = defineProps({
  progress: {
    type: Number,
    required: true
  },
  activePage: {
    type: String,
    default: ''
  }
})

const isHovering = ref(false)
const isCompact = ref(false)
const isMobileMenuOpen = ref(false)
const navRef = ref(null)
const compactMediaQuery = '(max-width: 900px), (max-aspect-ratio: 3/4)'
let compactMediaList

const currentPage = computed(() => {
  if (props.activePage) return props.activePage
  if (route.path.startsWith('/games')) return 'games'
  if (route.path.startsWith('/projects')) return 'projects'
  if (route.path.startsWith('/contact')) return 'contact'
  if (route.path === '/') return 'home'
  return ''
})

const navStyle = computed(() => {
  const baseOpacity = Math.max(0.5, 1 - props.progress * 0.5)
  return { opacity: isHovering.value ? 1 : baseOpacity }
})

const isNavigating = ref(false)

const updateCompactMode = () => {
  isCompact.value = compactMediaList?.matches ?? false
  if (!isCompact.value) {
    isMobileMenuOpen.value = false
  }
}

const closeMobileMenu = () => {
  if (isCompact.value) {
    isMobileMenuOpen.value = false
  }
}

const toggleMobileMenu = () => {
  if (!isCompact.value) return
  isMobileMenuOpen.value = !isMobileMenuOpen.value
}

const handleOutsidePress = (event) => {
  if (!isMobileMenuOpen.value || !navRef.value) return
  if (!navRef.value.contains(event.target)) {
    isMobileMenuOpen.value = false
  }
}

onMounted(() => {
  compactMediaList = window.matchMedia(compactMediaQuery)
  updateCompactMode()
  compactMediaList.addEventListener('change', updateCompactMode)
  document.addEventListener('pointerdown', handleOutsidePress)
})

onBeforeUnmount(() => {
  compactMediaList?.removeEventListener('change', updateCompactMode)
  document.removeEventListener('pointerdown', handleOutsidePress)
})

const scrollToTop = () => new Promise((resolve) => {
  const start = performance.now()
  const maxWait = 1200
  window.scrollTo({ top: 0, behavior: 'smooth' })

  const tick = (now) => {
    if (window.scrollY <= 1) {
      resolve()
      return
    }
    if (now - start >= maxWait) {
      resolve()
      return
    }
    requestAnimationFrame(tick)
  }

  requestAnimationFrame(tick)
})

const waitForProgressReset = () => new Promise((resolve) => {
  const start = performance.now()
  const maxWait = 2000
  const tick = (now) => {
    if (props.progress <= 0.001) {
      resolve()
      return
    }
    if (now - start >= maxWait) {
      resolve()
      return
    }
    requestAnimationFrame(tick)
  }

  requestAnimationFrame(tick)
})

const handleNavClick = async (key) => {
  if (isNavigating.value) return
  closeMobileMenu()
  isNavigating.value = true
  await scrollToTop()
  emit('request-reset')
  await waitForProgressReset()

  const targetPath = navTargets[key]
  if (targetPath && route.path !== targetPath) {
    try {
      await router.push(targetPath)
    } catch {
      // Ignore redundant navigation errors
    }
  }

  isNavigating.value = false
}
</script>

<style>
.top-nav {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 64px;
  background: rgba(30, 42, 58, 0.6);
  backdrop-filter: blur(10px);
  z-index: 10;
  transition: opacity 0.15s linear;
}

.nav-inner {
  position: relative;
  max-width: 1200px;
  height: 100%;
  margin: 0 auto;
  padding: 0 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 32px;
}

.menu-toggle {
  display: none;
}

.nav-scroll {
  position: relative;
  flex: 1 1 auto;
  min-width: 0;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  touch-action: pan-x;
}

.nav-scroll::-webkit-scrollbar {
  display: none;
}

.nav-scroll::before,
.nav-scroll::after {
  content: "";
  position: sticky;
  top: 0;
  width: 40px;
  height: 100%;
  pointer-events: none;
  z-index: 3;
  opacity: 0.9;
  filter: blur(2px);
}

.nav-scroll::before {
  left: 0;
  background: radial-gradient(circle at left center, rgba(255,255,255,0.85), rgba(30,42,58,0) 70%);
}

.nav-scroll::after {
  right: 0;
  background: radial-gradient(circle at right center, rgba(255,255,255,0.85), rgba(30,42,58,0) 70%);
}

.nav-links {
  display: flex;
  align-items: center;
  gap: 24px;
  list-style: none;
  margin: 0;
  padding: 0;
}

.nav-links li {
  height: 100%;
  display: flex;
  align-items: center;
  color: rgba(229,231,235,0.6);
  font-weight: 300;
  padding: 22px 18px;
  cursor: pointer;
  transition: color 0.2s ease, font-weight 0.2s ease;
}

.nav-links li:hover {
  color: var(--color-light);
  font-weight: 700;
}

.nav-links li.active {
  color: var(--color-light);
  font-weight: 700;
}

.nav-links li.nav-external {
  padding: 0;
}

.nav-links li.nav-external a {
  height: 100%;
  display: flex;
  align-items: center;
  padding: 22px 18px;
  color: inherit;
  font: inherit;
  text-decoration: none;
}

.lang-toggle {
  display: flex;
  height: 100%;
  cursor: pointer;
}

.lang-toggle span {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 20px;
  font-weight: 700;
}

.lang-toggle span.active:first-child {
  background: #16A34A;
  color: #FBBF24;
}

.lang-toggle span.active:last-child {
  background: #DC2626;
  color: #2563EB;
}

.mobile-menu-enter-active,
.mobile-menu-leave-active {
  transition: opacity 0.22s ease, transform 0.22s ease;
}

.mobile-menu-enter-from,
.mobile-menu-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

@media (min-aspect-ratio: 3/4) {
  .top-nav {
    height: 48px;
  }

  .nav-inner {
    padding: 0 12px;
    gap: 16px;
  }

  .nav-links {
    gap: 12px;
    flex-wrap: nowrap;
  }

  .nav-links li {
    font-size: 0.85rem;
    padding: 14px 12px;
    white-space: nowrap;
  }

  .nav-links li.nav-external {
    padding: 0;
  }

  .nav-links li.nav-external a {
    padding: 14px 12px;
  }

  .lang-toggle span {
    font-size: 0.85rem;
    padding: 0 12px;
    white-space: nowrap;
  }
}

@media (max-width: 900px), (max-aspect-ratio: 3/4) {
  .top-nav {
    height: 56px;
  }

  .nav-inner {
    padding: 0 12px;
    gap: 12px;
  }

  .menu-toggle {
    width: 40px;
    height: 40px;
    border: 0;
    border-radius: 8px;
    background: rgba(15, 23, 42, 0.55);
    display: inline-flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 5px;
    cursor: pointer;
    flex: 0 0 auto;
    transition: background 0.2s ease;
  }

  .menu-toggle span {
    width: 20px;
    height: 2px;
    background: #e5e7eb;
    transition: transform 0.22s ease, opacity 0.22s ease;
  }

  .menu-toggle.open span:nth-child(1) {
    transform: translateY(7px) rotate(45deg);
  }

  .menu-toggle.open span:nth-child(2) {
    opacity: 0;
  }

  .menu-toggle.open span:nth-child(3) {
    transform: translateY(-7px) rotate(-45deg);
  }

  .nav-scroll {
    position: absolute;
    top: calc(100% + 8px);
    left: 12px;
    right: 12px;
    overflow: visible;
    flex: 0 0 auto;
    min-width: auto;
    pointer-events: none;
  }

  .nav-scroll::before,
  .nav-scroll::after {
    display: none;
  }

  .nav-links {
    flex-direction: column;
    align-items: stretch;
    gap: 0;
    margin: 0;
    background: rgba(15, 23, 42, 0.94);
    border: 1px solid rgba(148, 163, 184, 0.35);
    border-radius: 10px;
    overflow: hidden;
    box-shadow: 0 14px 30px rgba(2, 6, 23, 0.45);
  }

  .nav-links li {
    height: auto;
    padding: 12px 14px;
    font-size: 0.95rem;
    border-bottom: 1px solid rgba(148, 163, 184, 0.22);
  }

  .nav-links li.nav-external {
    padding: 0;
  }

  .nav-links li.nav-external a {
    height: auto;
    padding: 12px 14px;
  }

  .nav-links li:last-child {
    border-bottom: 0;
  }

  .lang-toggle {
    margin-left: auto;
  }

  .lang-toggle span {
    padding: 0 14px;
    font-size: 0.8rem;
  }

  .nav-scroll.is-open {
    pointer-events: auto;
  }
}
</style>
